import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createProject,
  updateProject,
  deleteProject,
  updateProjectStatus,
} from '../services/projectsApi';
import { projectKeys } from './useProjects';
import type {
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectStatus,
} from '../types/project';

/**
 * Hook for project mutations (create, update, delete, status change)
 */
export const useProjectMutations = () => {
  const queryClient = useQueryClient();

  // Invalidate all project queries
  const invalidateProjects = () => {
    queryClient.invalidateQueries({ queryKey: projectKeys.all });
  };

  // Create project mutation
  const createProjectMutation = useMutation({
    mutationFn: (data: CreateProjectRequest) => createProject(data),
    onSuccess: () => {
      invalidateProjects();
    },
  });

  // Update project mutation
  const updateProjectMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectRequest }) =>
      updateProject(id, data),
    onSuccess: () => {
      invalidateProjects();
    },
  });

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: () => {
      invalidateProjects();
    },
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ProjectStatus }) =>
      updateProjectStatus(id, status),
    onSuccess: () => {
      invalidateProjects();
    },
  });

  return {
    createProject: createProjectMutation,
    updateProject: updateProjectMutation,
    deleteProject: deleteProjectMutation,
    updateStatus: updateStatusMutation,
    isLoading:
      createProjectMutation.isPending ||
      updateProjectMutation.isPending ||
      deleteProjectMutation.isPending ||
      updateStatusMutation.isPending,
  };
};

export default useProjectMutations;
